import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaCopy } from "react-icons/fa";
import swal from "sweetalert";
import { disable2FA, enable2FA, setup2FA } from "../../../../services/AuthService";
import "./AppProfile.css";

/**
 * Google Authenticator setup / disable UI for restaurant & franchise admins.
 */
export default function TwoFactorSettings({
  twoFactorEnabled,
  onEnabledChange,
  isActive = true,
}) {
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState("");
  const [manualSecret, setManualSecret] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [copied, setCopied] = useState(false);
  const setupStartedRef = useRef(false);

  const loadSetup = async () => {
    try {
      setIsSettingUp(true);
      const data = await setup2FA();
      setQrCode(data.qrCode || "");
      setManualSecret(data.secret || "");
      setSetupCode("");
      setCopied(false);
    } catch (err) {
      setupStartedRef.current = false;
      swal(
        "ERROR",
        err?.response?.data?.message ||
          t("twoFactorSetupError", {
            defaultValue: "Failed to start 2FA setup",
          }),
        "error"
      );
    } finally {
      setIsSettingUp(false);
    }
  };

  useEffect(() => {
    if (twoFactorEnabled) {
      setupStartedRef.current = false;
      return;
    }
    if (!isActive || setupStartedRef.current) return;
    setupStartedRef.current = true;
    void loadSetup();
  }, [isActive, twoFactorEnabled]);

  const handleEnable = async (event) => {
    event.preventDefault();
    if (!setupCode || setupCode.length < 6) {
      swal(
        "WARNING",
        t("authenticatorCodeRequired", {
          defaultValue: "Enter the 6-digit code from Google Authenticator",
        }),
        "warning"
      );
      return;
    }

    try {
      setIsEnabling(true);
      await enable2FA(setupCode.trim());
      onEnabledChange?.(true);
      setQrCode("");
      setManualSecret("");
      setSetupCode("");
      swal(
        "SUCCESS",
        t("twoFactorEnabledSuccess", {
          defaultValue:
            "Google Authenticator 2FA is now enabled. It will be required at login.",
        }),
        "success"
      );
    } catch (err) {
      swal(
        "ERROR",
        err?.response?.data?.message ||
          t("twoFactorEnableError", { defaultValue: "Failed to enable 2FA" }),
        "error"
      );
    } finally {
      setIsEnabling(false);
    }
  };

  const handleDisable = async (event) => {
    event.preventDefault();
    if (!disablePassword || !disableCode) {
      swal(
        "WARNING",
        t("twoFactorDisableRequired", {
          defaultValue:
            "Current password and authenticator code are required",
        }),
        "warning"
      );
      return;
    }

    try {
      setIsDisabling(true);
      await disable2FA(disableCode.trim(), disablePassword);
      onEnabledChange?.(false);
      setDisableCode("");
      setDisablePassword("");
      setQrCode("");
      setManualSecret("");
      swal(
        "SUCCESS",
        t("twoFactorDisabledSuccess", {
          defaultValue: "Two-factor authentication has been disabled.",
        }),
        "success"
      );
    } catch (err) {
      swal(
        "ERROR",
        err?.response?.data?.message ||
          t("twoFactorDisableError", {
            defaultValue: "Failed to disable 2FA",
          }),
        "error"
      );
    } finally {
      setIsDisabling(false);
    }
  };

  const copySecret = async () => {
    if (!manualSecret) return;
    try {
      await navigator.clipboard.writeText(manualSecret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="app-profile-account-panel app-profile-2fa pt-1">
      <div className="app-profile-2fa__head">
        <div className="app-profile-2fa__intro">
          <h4 className="app-profile-account-panel__title">
            {t("twoFactorAuthTitle", {
              defaultValue: "Google Authenticator 2FA",
            })}
          </h4>
          <p className="app-profile-account-panel__desc mb-0">
            {t("twoFactorAuthHelp", {
              defaultValue:
                "Protect your login with a one-time code from Google Authenticator.",
            })}
          </p>
        </div>
        <span
          className={`app-profile-2fa-badge ${
            twoFactorEnabled ? "is-on" : "is-off"
          }`}
        >
          {twoFactorEnabled
            ? t("enabled", { defaultValue: "Enabled" })
            : t("disabled", { defaultValue: "Disabled" })}
        </span>
      </div>

      {!twoFactorEnabled ? (
        <form onSubmit={handleEnable} className="app-profile-2fa-setup">
          <article className="app-profile-2fa-card">
            <div className="app-profile-2fa-card__label">
              <span className="app-profile-2fa-step">1</span>
              <h5>
                {t("twoFactorStepInstall", {
                  defaultValue: "Install Google Authenticator on your phone.",
                })}
              </h5>
            </div>
            <p className="app-profile-2fa-hint mb-0">
              {t("twoFactorInstallHelp", {
                defaultValue: "Get the app from the App Store or Google Play, then continue below.",
              })}
            </p>
          </article>

          <div className="app-profile-2fa-setup__grid">
              <section className="app-profile-2fa-card app-profile-2fa-qr-card">
                <div className="app-profile-2fa-card__label">
                  <span className="app-profile-2fa-step">2</span>
                  <h5>
                    {t("twoFactorScanTitle", {
                      defaultValue: "Scan this QR code",
                    })}
                  </h5>
                </div>
                <div className="app-profile-2fa-qr">
                  {qrCode ? (
                    <img
                      src={qrCode}
                      alt=""
                      className="app-profile-2fa-qr__img"
                    />
                  ) : (
                    <div className="app-profile-2fa-qr__loading">
                      {t("generatingQr", { defaultValue: "Generating QR..." })}
                    </div>
                  )}
                </div>
              </section>

              <section className="app-profile-2fa-card app-profile-2fa-verify-card">
                <div className="app-profile-2fa-card__label">
                  <span className="app-profile-2fa-step">3</span>
                  <h5>
                    {t("twoFactorVerifyTitle", {
                      defaultValue: "Confirm with a code",
                    })}
                  </h5>
                </div>

                <div className="app-profile-2fa-field">
                  <label className="form-label">
                    {t("manualKey", {
                      defaultValue: "Manual key (if you cannot scan)",
                    })}
                  </label>
                  <div className="app-profile-2fa-secret">
                    <input
                      type="text"
                      className="form-control"
                      value={manualSecret}
                      readOnly
                      onFocus={(e) => e.target.select()}
                      placeholder={
                        isSettingUp
                          ? t("generatingQr", { defaultValue: "Generating QR..." })
                          : ""
                      }
                    />
                    <button
                      type="button"
                      className="app-profile-2fa-copy"
                      onClick={copySecret}
                      disabled={!manualSecret}
                      aria-label={
                        copied
                          ? t("copied", { defaultValue: "Copied" })
                          : t("copy", { defaultValue: "Copy" })
                      }
                    >
                      {copied ? <FaCheck aria-hidden /> : <FaCopy aria-hidden />}
                      {copied
                        ? t("copied", { defaultValue: "Copied" })
                        : t("copy", { defaultValue: "Copy" })}
                    </button>
                  </div>
                </div>

                <div className="app-profile-2fa-field">
                  <label className="form-label">
                    {t("authenticatorCodeTitle", {
                      defaultValue: "Authenticator code",
                    })}
                  </label>
                  <input
                    type="text"
                    className="form-control app-profile-2fa-code"
                    placeholder="000000"
                    value={setupCode}
                    onChange={(e) =>
                      setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                  <p className="app-profile-2fa-hint">
                    {t("authenticatorCodeHelp", {
                      defaultValue:
                        "Open Google Authenticator and enter the 6-digit code for your account.",
                    })}
                  </p>
                </div>
              </section>
            </div>

          <div className="app-profile-2fa-card__actions app-profile-2fa-setup__actions">
            <button
              type="button"
              className="btn btn-outline-secondary app-profile-btn-ghost"
              onClick={loadSetup}
              disabled={isSettingUp}
            >
              {t("regenerateQr", { defaultValue: "Regenerate QR" })}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isEnabling || !qrCode}
            >
              {isEnabling
                ? t("enabling", { defaultValue: "Enabling..." })
                : t("enable2FA", { defaultValue: "Enable 2FA" })}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleDisable} className="app-profile-2fa-disable">
          <article className="app-profile-2fa-card">
            <p className="app-profile-2fa-protected">
              {t("twoFactorProtected", {
                defaultValue:
                  "Your account is protected. 2FA is required at login.",
              })}
            </p>
            <p className="app-profile-2fa-hint">
              {t("twoFactorDisableHelp", {
                defaultValue:
                  "Enter your password and a current authenticator code to turn it off.",
              })}
            </p>
            <div className="app-profile-2fa-disable__grid">
              <div className="app-profile-2fa-field">
                <label className="form-label">{t("oldPwd")}</label>
                <input
                  type="password"
                  className="form-control"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <div className="app-profile-2fa-field">
                <label className="form-label">
                  {t("authenticatorCodeTitle", {
                    defaultValue: "Authenticator code",
                  })}
                </label>
                <input
                  type="text"
                  className="form-control app-profile-2fa-code"
                  placeholder="000000"
                  value={disableCode}
                  onChange={(e) =>
                    setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              </div>
            </div>
            <div className="app-profile-2fa-card__actions">
              <button
                type="submit"
                className="btn btn-outline-danger app-profile-btn-danger"
                disabled={isDisabling}
              >
                {isDisabling
                  ? t("disabling", { defaultValue: "Disabling..." })
                  : t("disable2FA", { defaultValue: "Disable 2FA" })}
              </button>
            </div>
          </article>
        </form>
      )}
    </div>
  );
}
